import { AppSidebar } from "@/components/common/app-sidebar";
import Header from "@/components/common/header";
import LoadingSpinner from "@/components/common/pages/loading-spinner";
import NotFound from "@/components/common/pages/not-found";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import WhatsAppReconnectGate from "@/components/whatsapp-reconnect-modal/gate";
import { appErrorPaths, isAppErrorPath } from "@/lib/app-error-routes";
import { getProtectedRouteAuthDecision } from "@/lib/auth-status";
import { getStoredSupplierRole, hasStoredUser } from "@/lib/auth-storage";
import { useAuth } from "@/providers/auth";
import {
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
} from "@tanstack/react-router";

function isWarehouseAllowedPath(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/orders" ||
    pathname === "/orders/" ||
    pathname === "/products" ||
    pathname === "/products/" ||
    pathname === "/reports" ||
    pathname === "/reports/" ||
    pathname === "/reports/oos" ||
    pathname === "/reports/oos/" ||
    pathname.startsWith("/products/upload") ||
    (pathname.startsWith("/orders/") &&
      !pathname.startsWith("/orders/upload") &&
      !pathname.startsWith("/orders/generate-invoice"))
  );
}

export const Route = createFileRoute("/_main")({
  component: RouteComponent,
  beforeLoad: async ({ context, location }) => {
    if (isAppErrorPath(location.pathname)) {
      await context.auth.checkAuthentication();
      return;
    }

    const hadStoredUser = hasStoredUser();
    const authStatus = await context.auth.checkAuthentication();
    const decision = getProtectedRouteAuthDecision(authStatus, hadStoredUser);

    if (decision === "session-expired") {
      throw redirect({
        to: appErrorPaths["session-expired"],
        search: {
          from: location.href,
        },
      });
    }

    if (decision === "sign-in") {
      throw redirect({
        to: "/sign-in",
        search: {
          redirect: location.href,
        },
      });
    }

    const supplierRole =
      context.auth.user?.supplierRole ?? getStoredSupplierRole();
    if (
      supplierRole === "warehouse_user" &&
      !isWarehouseAllowedPath(location.pathname)
    ) {
      throw redirect({
        to: "/",
      });
    }
  },
  notFoundComponent: () => <NotFound />,
  pendingComponent: () => (
    <div className="flex h-screen items-center justify-center">
      <LoadingSpinner size={50} />
    </div>
  ),
  pendingMs: 25,
});

function RouteComponent() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { isAuthenticated, user } = useAuth();
  const shouldRenderPlainErrorScreen =
    isAppErrorPath(pathname) && !isAuthenticated && !user;

  if (shouldRenderPlainErrorScreen) {
    return <Outlet />;
  }

  return (
    <div className="flex h-screen flex-col">
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <Header />
          <WhatsAppReconnectGate />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
